/**
 * THE RULE: every money value, everywhere — Firestore, memory, function
 * arguments, Cloud Functions — is an INTEGER NUMBER OF PAISE. Rupees exist
 * only on a screen and in a form field a human types into.
 *
 * Where a division creates a fraction of a paisa, THE COLLABORATOR GETS IT.
 * splitPayout() below IS that rule, and it is duplicated VERBATIM in
 * functions/src/payouts.ts. Change one, change both, same commit.
 */

export type Paise = number;

export interface Money {
  amount: Paise;
  currency: 'INR';
}

export interface PointValue {
  points: number;
  ratePerPoint: Money;
}

export const PLATFORM_COMMISSION_NUMERATOR = 10;
export const PLATFORM_COMMISSION_DENOMINATOR = 100;

/**
 * Buyer-side markup, as an integer scaled by 10,000.
 * 11000 = 1.1000 = "buyer pays 10% on top of collaborator cost".
 * Never a float — a markup of 1.1 multiplied against paise reintroduces
 * exactly the rounding error the paise discipline exists to prevent.
 */
export const MARKUP_SCALE = 10000;
export const DEFAULT_BUYER_MARKUP = 11000;
export const MIN_BUYER_MARKUP = 10000;
export const MAX_BUYER_MARKUP = 30000;

/** 11000 -> 1.1 (display only). Never multiply money by this. */
export function markupToMultiplier(markup: number): number {
  return markup / MARKUP_SCALE;
}

/** 1.1 (from an admin form) -> 11000. */
export function multiplierToMarkup(multiplier: number): number {
  return Math.round(multiplier * MARKUP_SCALE);
}

export function clampMarkup(markup: number): number {
  if (!Number.isFinite(markup)) return DEFAULT_BUYER_MARKUP;
  return Math.min(MAX_BUYER_MARKUP, Math.max(MIN_BUYER_MARKUP, Math.round(markup)));
}

/**
 * The floor is on the PLATFORM's cut, never the partner's payout — that is
 * what makes the fractional paisa fall to the partner every time.
 *   earning 1001 -> cut floor(100.1) = 100 -> payout 901
 */
export function splitPayout(earningPaise: Paise): {
  payoutPaise: Paise;
  platformCutPaise: Paise;
} {
  const earning = Math.round(earningPaise);
  const platformCutPaise = Math.floor(
    (earning * PLATFORM_COMMISSION_NUMERATOR) / PLATFORM_COMMISSION_DENOMINATOR,
  );
  return { payoutPaise: earning - platformCutPaise, platformCutPaise };
}

/** points x rate, integer. Gross earning, BEFORE commission. */
export function earningPaise(points: number, ratePerPointPaise: Paise): Paise {
  return Math.round(points * ratePerPointPaise);
}

/** Paise -> rupees. DISPLAY ONLY. Never feed back into a calculation. */
export function rupees(paise: Paise): number {
  return paise / 100;
}

/** Rupees (from a human, in a form) -> integer paise. The Math.round is the
 *  float defence: 4500.55 * 100 is 450054.99999999994 without it. */
export function toPaise(inr: number): Paise {
  return Math.round(inr * 100);
}

/**
 * `exact: true` shows paise. Use it ANYWHERE a human acts on the number —
 * the admin release screen, a partner's payout row. The default rounds to
 * whole rupees, which is fine for a headline and a lie on a transfer screen.
 */
export function formatPaise(paise: Paise, opts?: { exact?: boolean }): string {
  const exact = opts?.exact === true;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: exact ? 2 : 0,
    maximumFractionDigits: exact ? 2 : 0,
  }).format(rupees(paise ?? 0));
}